<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue';
import { useSystemPrompts } from '../../composables/useSystemPrompts';
import type { RouteLocationNormalized } from 'vue-router';

// Props
interface Props {
  currentRoute?: RouteLocationNormalized;
  currentUser?: {
    id: number;
    email: string;
    company_id: number;
    company?: {
      id: number;
      name: string;
      role: string;
    };
    first_name?: string;
  } | null;
}

const props = withDefaults(defineProps<Props>(), {
  currentRoute: undefined,
  currentUser: null
});

// Initialize system prompts composable
const { generateSystemPrompt, extractUserContext } = useSystemPrompts();


interface CustomAIChatConfig {
  apiUrl: string;
  theme: string;
  position: string;
  title: string;
  subtitle: string;
  logoUrl: string;
  site: string;
  placeHolderText: string;
  systemInfo: string;
}

declare global {
  interface Window {
    chatManagerCleanup?: () => void;
    customAIChatConfig?: CustomAIChatConfig;
  }
}

let initialViewportHeight = window.innerHeight;
let keyboardHandlerCleanup: (() => void) | null = null;
let orientationTimeout: ReturnType<typeof setTimeout> | null = null;

const addMobileKeyboardHandler = (): void => {
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
  if (!isMobile) return;

  // Hot fix: reload page on orientation change to fix scroll lock issues
  let currentOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';

  const handleOrientationChange = () => {
    const newOrientation = window.innerWidth > window.innerHeight ? 'landscape' : 'portrait';
    if (newOrientation !== currentOrientation) {
      // Clear any existing timeout before setting a new one
      if (orientationTimeout) {
        clearTimeout(orientationTimeout);
      }

      // Small delay to ensure orientation change is complete
      orientationTimeout = setTimeout(() => {
        window.location.reload();
        orientationTimeout = null;
      }, 100);
    }
  };

  const handleViewportChange = () => {
    const currentHeight = window.visualViewport?.height || window.innerHeight;
    const heightDifference = initialViewportHeight - currentHeight;
    const keyboardThreshold = 150; // Minimum height change to consider keyboard open
    const isLandscape = window.innerWidth > window.innerHeight;

    const chatWindow = document.querySelector('.embed-chat-window') as HTMLElement;
    if (!chatWindow) return;

    if (heightDifference > keyboardThreshold) {
      // Keyboard is likely open
      if (isLandscape) {
        // In landscape mode, set to 100% height
        chatWindow.style.setProperty('height', '100%', 'important');
      } else {
        // In portrait mode, use 48vh
        chatWindow.style.setProperty('height', '48vh', 'important');
      }
      chatWindow.style.setProperty('bottom', '0px', 'important');
      chatWindow.style.setProperty('border-radius', '16px 16px 0 0', 'important');
    } else {
      // Keyboard is likely closed - restore normal size
      chatWindow.style.removeProperty('height');
      chatWindow.style.removeProperty('bottom');
      chatWindow.style.removeProperty('border-radius');
    }
  };

  // Listen for viewport changes
  window.addEventListener('resize', handleViewportChange);

  // Listen for orientation changes (hotfix for scroll lock)
  window.addEventListener('resize', handleOrientationChange);

  // Also listen for visual viewport changes (better for mobile keyboards)
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', handleViewportChange);
  }

  // Store cleanup function
  keyboardHandlerCleanup = () => {
    // Clear any pending orientation timeout
    if (orientationTimeout) {
      clearTimeout(orientationTimeout);
      orientationTimeout = null;
    }

    window.removeEventListener('resize', handleViewportChange);
    window.removeEventListener('resize', handleOrientationChange);
    if (window.visualViewport) {
      window.visualViewport.removeEventListener('resize', handleViewportChange);
    }
  };
};

const cleanupChat = () => {
  // Clear orientation timeout first (belt and suspenders approach)
  if (orientationTimeout) {
    clearTimeout(orientationTimeout);
    orientationTimeout = null;
  }

  // Clean up event listeners if they exist
  if (window.chatManagerCleanup) {
    window.chatManagerCleanup();
    delete window.chatManagerCleanup;
  }

  // Clean up keyboard handler
  if (keyboardHandlerCleanup) {
    keyboardHandlerCleanup();
    keyboardHandlerCleanup = null;
  }

  // Clean up mobile styles
  const mobileStyles = document.getElementById('mobile-chat-styles');
  if (mobileStyles) {
    mobileStyles.remove();
  }

  // Reset body styles - wrapped in try-catch for safety
  try {
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.width = '';
    document.body.classList.remove('chat-open');
  } catch (error) {
    console.warn('Error resetting body styles:', error);
  }
};

const userContext = extractUserContext(props.currentRoute, props.currentUser);
const systemPrompt = generateSystemPrompt(userContext);

const systemInfo = `
  SYSTEM:\n
  -User: ${props.currentUser?.first_name || 'Unknown'}\n
  -Company: ${props.currentUser?.company?.name || 'Unknown'}\n
  -Currently Browsing: ${String(props.currentRoute?.name) || 'Unknown Page'}\n\n
  ${systemPrompt}\n\n
`;

const initChatWidget = () => {
  window.customAIChatConfig = {
    apiUrl: 'https://customsoftwaresolutions.co.nz',
    theme: 'light',
    position: 'bottom-right',
    title: 'Mandy',
    subtitle: 'Online now',
    logoUrl: 'https://www.integrateme.co.nz/vite/assets/logo-integrate-me-CXn4ATuK.png',
    placeHolderText: '👋 Welcome! How can we help you today?',
    systemInfo,
    site: 'integrate_me'
  };

  if (!document.getElementById('chat-widget-script')) {
    const script = document.createElement('script');
    script.id = 'chat-widget-script';
    script.src = 'https://customsoftwaresolutions.co.nz/embed/chat-widget.js';
    document.body.appendChild(script);
  }

  setTimeout(() => {
    addMobileKeyboardHandler();
  }, 1000);
};

onMounted(() => {
  initChatWidget();
});

onUnmounted(() => {
  cleanupChat();
});
</script>
<template>
  <!-- Dynmically generated from CAIS -->
</template>
